import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';

/**
 * HeroCropOverlay — shared crop UI for project hero images.
 *
 * Extracted from the inline CropOverlay component in ProjectDetailPage
 * (Path Y, hero unification). Pure presentational; owns only its own
 * zoom / pan / viewport state. The parent supplies a CropState (source
 * image URL + natural dimensions) and receives back a JPEG data URL via
 * onConfirm, or nothing via onCancel.
 *
 * The output canvas matches the parent container's aspect ratio (measured
 * at runtime) so that downstream object-fit:cover renders don't re-zoom
 * the cropped image.
 *
 * Renders absolutely-positioned over its parent — caller must place this
 * inside a positioned hero container. See PillarPageShell's heroCropOverlay
 * prop for the established mounting point.
 *
 * NOTE: Style literals (rgba(...), hex colors) carried over from the
 * source component verbatim to preserve visual fidelity on photo
 * backgrounds where token-based palettes don't map cleanly. Tokenization
 * is a separate cleanup ticket.
 */

const JPEG_QUALITY = 0.92;
const HERO_HEIGHT = 280;
const CROP_OUTPUT_W = 1920;

export interface CropState {
  src: string;
  naturalW: number;
  naturalH: number;
}

export interface HeroCropOverlayProps {
  crop: CropState;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}

export default function HeroCropOverlay({
  crop,
  onConfirm,
  onCancel,
}: HeroCropOverlayProps) {
  const { t } = useAppTranslation('pages');
  const overlayRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(400);
  const [viewportH, setViewportH] = useState(HERO_HEIGHT);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);

  // Measure the overlay's parent container (hero banner) so the output
  // canvas matches its aspect ratio and object-fit:cover won't re-zoom.
  const computedTargetAspect = useMemo(() => {
    const parent = overlayRef.current?.parentElement;
    if (parent && parent.clientWidth > 0 && parent.clientHeight > 0) {
      return parent.clientWidth / parent.clientHeight;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportW]); // viewportW as proxy for "layout settled"

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setViewportW(e.contentRect.width);
        setViewportH(e.contentRect.height);
      }
    });
    ro.observe(el);
    setViewportW(el.clientWidth);
    setViewportH(el.clientHeight || HERO_HEIGHT);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const { naturalW, naturalH } = crop;
  const baseScale = Math.max(viewportW / naturalW, viewportH / naturalH);
  const displayScale = baseScale * zoom;
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;

  const clampPan = useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const maxPanX = Math.max(0, (dw - viewportW) / 2);
      const maxPanY = Math.max(0, (dh - viewportH) / 2);
      return {
        x: Math.max(-maxPanX, Math.min(maxPanX, px)),
        y: Math.max(-maxPanY, Math.min(maxPanY, py)),
      };
    },
    [baseScale, naturalW, naturalH, viewportW, viewportH, zoom],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPanX: pan.x,
        startPanY: pan.y,
      };
    },
    [pan],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPan(clampPan(dragRef.current.startPanX + dx, dragRef.current.startPanY + dy));
    },
    [clampPan],
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const next = Math.max(1, Math.min(4, zoom - e.deltaY * 0.002));
      setZoom(next);
      setPan((p) => clampPan(p.x, p.y, next));
    },
    [zoom, clampPan],
  );

  const handleZoomSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseFloat(e.target.value);
      setZoom(next);
      setPan((p) => clampPan(p.x, p.y, next));
    },
    [clampPan],
  );

  const handleConfirm = useCallback(() => {
    // Use the parent container's aspect ratio so the output image matches
    // the display area and object-fit:cover doesn't further zoom the result.
    const aspect =
      computedTargetAspect && computedTargetAspect > 0
        ? computedTargetAspect
        : viewportW / viewportH;

    // Source region visible in the viewport
    const visSrcW = viewportW / displayScale;
    const visSrcH = viewportH / displayScale;
    const visSrcCX = (displayW / 2 - pan.x) / displayScale;
    const visSrcCY = (displayH / 2 - pan.y) / displayScale;

    // Expand/contract the source region to match target aspect
    let srcW: number;
    let srcH: number;
    if (visSrcW / visSrcH > aspect) {
      srcW = visSrcW;
      srcH = visSrcW / aspect;
    } else {
      srcH = visSrcH;
      srcW = visSrcH * aspect;
    }

    const srcX = Math.max(0, Math.min(naturalW - srcW, visSrcCX - srcW / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcH, visSrcCY - srcH / 2));

    const outW = CROP_OUTPUT_W;
    const outH = Math.round(CROP_OUTPUT_W / aspect);
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.src = crop.src;
  }, [
    crop.src,
    naturalW,
    naturalH,
    displayW,
    displayH,
    displayScale,
    pan,
    viewportW,
    viewportH,
    computedTargetAspect,
    onConfirm,
  ]);

  const imgStyle: React.CSSProperties = {
    position: 'absolute',
    width: displayW,
    height: displayH,
    left: (viewportW - displayW) / 2 + pan.x,
    top: (viewportH - displayH) / 2 + pan.y,
    userSelect: 'none',
    pointerEvents: 'none',
  };

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        background: 'var(--bg-elevated)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{t('heroEdit.dragToReposition')}</span>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          {Math.round(zoom * 100)}%
        </span>
      </div>
      <div
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: dragRef.current ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <img src={crop.src} alt="Crop preview" draggable={false} style={imgStyle} />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {[1, 2].map((i) => (
            <React.Fragment key={i}>
              <div
                style={{
                  position: 'absolute',
                  left: `${(i / 3) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: 'rgba(255,255,255,0.15)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${(i / 3) * 100}%`,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: 'rgba(255,255,255,0.15)',
                }}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div
        style={{
          padding: '8px 12px',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>
          {t('heroEdit.zoom')}
        </span>
        <input
          type="range"
          min="1"
          max="4"
          step="0.01"
          value={zoom}
          onChange={handleZoomSlider}
          style={{
            flex: 1,
            accentColor: 'var(--accent-secondary)',
            cursor: 'pointer',
          }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: 'var(--bg-hover)',
              border: '1px solid var(--border-default)',
              borderRadius: 6,
              color: 'var(--text-secondary)',
              fontSize: 12,
              fontWeight: 600,
              padding: '5px 14px',
              cursor: 'pointer',
            }}
          >
            {t('heroEdit.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              background: 'rgba(69,187,71,0.75)',
              border: '1px solid var(--border-default)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontSize: 12,
              fontWeight: 600,
              padding: '5px 14px',
              cursor: 'pointer',
            }}
          >
            {t('heroEdit.saveCrop')}
          </button>
        </div>
      </div>
    </div>
  );
}
